'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function OvulationCalculator() {
  const t = useTranslations("tools.ovulationCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  const [inputs, setInputs] = useState({
    lastPeriod: '',
    cycleLength: 28
  });

  const [results, setResults] = useState<{
    cycleLength: number;
    lastPeriodDate: Date;
    nextPeriodDate: Date;
    ovulationDate: Date;
    fertileDays: Date[];
    nextCycles: Array<{
      cycleNumber: number;
      periodStart: Date;
      ovulation: Date;
      fertileStart: Date;
    }>;
  } | null>(null);

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  useEffect(() => {
    const today = new Date();
    const defaultDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 14);
    const formattedDate = defaultDate.toISOString().split('T')[0];
    setInputs(prev => ({ ...prev, lastPeriod: formattedDate }));
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const formatDate = (date: Date): string => {
    const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'long', day: 'numeric' };
    return date.toLocaleDateString('en-US', options);
  };

  const formatShortDate = (date: Date): string => {
    const options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
    return date.toLocaleDateString('en-US', options);
  };

  const addDays = (date: Date, days: number): Date => {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
  };

  const calculateOvulation = (lastPeriodDate: Date, cycleLength: number): Date => {
    const ovulationDay = cycleLength - 14;
    return addDays(lastPeriodDate, ovulationDay);
  };

  const calculateFertileWindow = (ovulationDate: Date): Date[] => {
    const fertileDays: Date[] = [];
    for (let i = -5; i <= 0; i++) {
      fertileDays.push(addDays(ovulationDate, i));
    }
    return fertileDays;
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();

    if (!inputs.lastPeriod) {
      return;
    }

    const lastPeriodDate = new Date(inputs.lastPeriod);
    const ovulationDate = calculateOvulation(lastPeriodDate, inputs.cycleLength);
    const fertileDays = calculateFertileWindow(ovulationDate);
    const nextPeriodDate = addDays(lastPeriodDate, inputs.cycleLength);

    const nextCycles = [];
    for (let i = 1; i <= 6; i++) {
      const cycleStart = addDays(lastPeriodDate, inputs.cycleLength * i);
      const cycleOvulation = calculateOvulation(cycleStart, inputs.cycleLength);
      const cycleFertileStart = addDays(cycleOvulation, -5);

      nextCycles.push({
        cycleNumber: i + 1,
        periodStart: cycleStart,
        ovulation: cycleOvulation,
        fertileStart: cycleFertileStart
      });
    }

    setResults({
      cycleLength: inputs.cycleLength,
      lastPeriodDate,
      nextPeriodDate,
      ovulationDate,
      fertileDays,
      nextCycles
    });
  };

  const colors = {
    primary: '#be123c',
    primaryLight: '#fecdd3',
    primaryDark: '#881337',
    fertile: '#10b981',
    fertileLight: '#a7f3d0',
    fertileDark: '#065f46',
    ovulation: '#f59e0b',
    ovulationLight: '#fde68a',
    ovulationDark: '#92400e',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    white: '#ffffff',
    background: '#fff1f2',
  };

  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6, marginBottom: '0' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #fef3f2 0%, #fce7f3 100%)',
      borderRadius: '1rem',
      marginBottom: '2rem'
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem' },
    { padding: '3rem 1.5rem' }
  );

  const toolContainerStyle = getResponsiveStyle(
    { maxWidth: '100%', margin: '0 auto' },
    {},
    { maxWidth: '700px' },
    { maxWidth: '900px' }
  );

  const toolHeaderStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '2.5rem' },
    {}
  );

  const h2Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.primary, marginBottom: '0.5rem', lineHeight: 1.2 },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const toolSubtitleStyle = getResponsiveStyle(
    { color: colors.primaryDark, margin: '0' },
    { fontSize: '0.875rem' },
    { fontSize: '1rem' },
    { fontSize: '1rem' }
  );

  const calculatorFormStyle = getResponsiveStyle(
    {
      background: colors.white,
      borderRadius: '1rem',
      boxShadow: '0 10px 30px rgba(190, 18, 60, 0.1)',
      marginBottom: '2rem'
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2.5rem' }
  );

  const formGroupStyle = getResponsiveStyle(
    {},
    { marginBottom: '1.5rem' },
    { marginBottom: '1.75rem' },
    { marginBottom: '1.75rem' }
  );

  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontWeight: 600,
    color: colors.primaryDark,
    marginBottom: '0.5rem',
    fontSize: '1rem'
  };

  const inputStyle = getResponsiveStyle(
    {
      width: '100%',
      border: `2px solid ${colors.primaryLight}`,
      borderRadius: '0.5rem',
      transition: 'all 0.3s ease',
      background: colors.white,
      color: '#333',
      boxSizing: 'border-box' as const
    },
    { padding: '0.75rem', fontSize: '1rem' },
    { padding: '0.875rem', fontSize: '1rem' },
    { padding: '0.875rem', fontSize: '1rem' }
  );

  const calculateButtonStyle = getResponsiveStyle(
    {
      width: '100%',
      background: `linear-gradient(135deg, ${colors.primary} 0%, #e11d48 100%)`,
      color: colors.white,
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      marginTop: '1rem'
    },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' },
    { padding: '1rem 2rem', fontSize: '1.125rem' },
    { padding: '1rem 2rem', fontSize: '1.125rem' }
  );

  const resultsStyle = getResponsiveStyle(
    {
      background: colors.white,
      borderRadius: '1rem',
      boxShadow: '0 10px 30px rgba(190, 18, 60, 0.1)'
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2.5rem' }
  );

  const resultsH3Style = getResponsiveStyle(
    { color: colors.primary, margin: '0 0 1.5rem 0', textAlign: 'center' as const, fontWeight: 700 },
    { fontSize: '1.25rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.5rem' }
  );

  const cycleInfoStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #fff1f2 0%, #ffe4e6 100%)',
      borderRadius: '0.75rem',
      marginBottom: '2rem',
      borderLeft: `4px solid ${colors.primary}`
    },
    { padding: '1rem' },
    { padding: '1.25rem' },
    { padding: '1.5rem' }
  );

  const cycleInfoItemStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.75rem 0',
    borderBottom: `1px solid ${colors.primaryLight}`
  };

  const cycleInfoLabelStyle = getResponsiveStyle(
    { fontWeight: 600, color: colors.primaryDark },
    { fontSize: '0.875rem' },
    { fontSize: '0.95rem' },
    { fontSize: '0.95rem' }
  );

  const cycleInfoValueStyle = getResponsiveStyle(
    { fontWeight: 700, color: colors.primary },
    { fontSize: '0.875rem' },
    { fontSize: '1rem' },
    { fontSize: '1rem' }
  );

  const ovulationDayStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
      borderRadius: '0.75rem',
      marginBottom: '2rem',
      borderLeft: `4px solid ${colors.ovulation}`,
      textAlign: 'center' as const
    },
    { padding: '1rem' },
    { padding: '1.25rem' },
    { padding: '1.5rem' }
  );

  const ovulationH4Style = getResponsiveStyle(
    { color: colors.ovulationDark, margin: '0 0 0.75rem 0' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.125rem' }
  );

  const ovulationDateStyle = getResponsiveStyle(
    { fontWeight: 700, color: colors.ovulation, marginBottom: '0.5rem' },
    { fontSize: '1.25rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.5rem' }
  );

  const ovulationNoteStyle = getResponsiveStyle(
    { color: colors.ovulationDark, fontStyle: 'italic' as const },
    { fontSize: '0.75rem' },
    { fontSize: '0.875rem' },
    { fontSize: '0.875rem' }
  );

  const fertileWindowStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)',
      borderRadius: '0.75rem',
      marginBottom: '2rem',
      borderLeft: `4px solid ${colors.fertile}`
    },
    { padding: '1rem' },
    { padding: '1.25rem' },
    { padding: '1.5rem' }
  );

  const fertileH4Style = getResponsiveStyle(
    { color: colors.fertileDark, margin: '0 0 1rem 0', display: 'flex', alignItems: 'center', gap: '0.5rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.125rem' }
  );

  const fertileDescStyle = getResponsiveStyle(
    { margin: '0 0 1rem 0', color: colors.fertileDark },
    { fontSize: '0.8rem' },
    { fontSize: '0.9rem' },
    { fontSize: '0.9rem' }
  );

  const fertileDatesStyle = getResponsiveStyle(
    { display: 'grid', gap: '0.75rem', marginTop: '1rem' },
    { gridTemplateColumns: 'repeat(2, 1fr)' },
    { gridTemplateColumns: 'repeat(3, 1fr)' },
    { gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))' }
  );

  const fertileDateStyle: React.CSSProperties = {
    background: colors.white,
    padding: '0.75rem',
    borderRadius: '0.5rem',
    textAlign: 'center',
    border: `2px solid #6ee7b7`
  };

  const fertileDateLabelStyle: React.CSSProperties = {
    fontSize: '0.75rem',
    color: colors.fertileDark,
    fontWeight: 600,
    marginBottom: '0.25rem'
  };

  const fertileDateValueStyle: React.CSSProperties = {
    fontSize: '0.875rem',
    color: colors.fertile,
    fontWeight: 700
  };

  const nextPeriodsStyle = getResponsiveStyle(
    { marginTop: '2rem', paddingTop: '2rem', borderTop: `2px solid ${colors.primaryLight}` },
    {},
    {},
    {}
  );

  const nextPeriodsH4Style = getResponsiveStyle(
    { color: colors.primaryDark, margin: '0 0 1rem 0' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.125rem' }
  );

  const periodListStyle: React.CSSProperties = {
    display: 'grid',
    gap: '0.75rem'
  };

  const periodItemStyle = getResponsiveStyle(
    {
      background: '#fff1f2',
      borderRadius: '0.5rem',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      flexWrap: 'wrap' as const
    },
    { padding: '0.75rem', gap: '0.5rem' },
    { padding: '1rem', gap: '0.75rem' },
    { padding: '1rem', gap: '1rem' }
  );

  const periodCycleStyle: React.CSSProperties = {
    fontWeight: 600,
    color: colors.primaryDark
  };

  const periodDatesStyle = getResponsiveStyle(
    { color: colors.primary, fontWeight: 700 },
    { fontSize: '0.75rem' },
    { fontSize: '0.875rem' },
    { fontSize: '0.875rem' }
  );

  const legendStyle = getResponsiveStyle(
    { display: 'flex', justifyContent: 'center', gap: '1.5rem', marginTop: '1.5rem', flexWrap: 'wrap' as const },
    {},
    {},
    {}
  );

  const legendItemStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    fontSize: '0.875rem'
  };

  const legendColorStyle: React.CSSProperties = {
    width: '20px',
    height: '20px',
    borderRadius: '0.25rem'
  };

  const glassCardStyle = getResponsiveStyle(
    {
      background: 'rgba(255, 255, 255, 0.6)',
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: '1px solid rgba(255, 200, 180, 0.2)',
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
      marginBottom: '2rem'
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2.5rem' }
  );

  const h2GlassStyle = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    { lineHeight: 1.7, color: colors.textSecondary, marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.125rem' }
  );

  const listStyle = getResponsiveStyle(
    { lineHeight: 1.8, color: colors.textSecondary, paddingLeft: '1.5rem', marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.125rem' }
  );

  const listItemStyle: React.CSSProperties = {
    marginBottom: '0.75rem'
  };

  const strongStyle: React.CSSProperties = {
    color: colors.primary,
    fontWeight: 600
  };

  const faqItemStyle = getResponsiveStyle(
    { marginBottom: '1.5rem' },
    {},
    { marginBottom: '2rem' },
    { marginBottom: '2rem' }
  );

  const faqQuestionStyle = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '0.5rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' },
    { fontSize: '1.25rem' }
  );

  const faqAnswerStyle = getResponsiveStyle(
    { lineHeight: 1.7, color: colors.textSecondary },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.125rem' }
  );

  const referenceListStyle: React.CSSProperties = {
    listStyleType: 'none',
    padding: 0,
    margin: 0
  };

  const referenceItemStyle: React.CSSProperties = {
    marginBottom: '0.75rem',
    fontSize: '0.9rem',
    color: colors.textSecondary
  };

  const referenceLinkStyle: React.CSSProperties = {
    color: colors.primary,
    textDecoration: 'none',
    wordBreak: 'break-word'
  };

  return (
    <div style={containerStyle}>
      <header style={headerStyle}>
        <h1 style={h1Style}>{t("title")}</h1>
        <p style={summaryTextStyle}>{t("subtitle")}</p>
      </header>

      <section style={calculatorToolStyle}>
        <div style={toolContainerStyle}>
          <div style={toolHeaderStyle}>
            <h2 style={h2Style}>{t("toolTitle")}</h2>
            <p style={toolSubtitleStyle}>{t("toolSubtitle")}</p>
          </div>

          <form style={calculatorFormStyle} onSubmit={handleCalculate}>
            <div style={formGroupStyle}>
              <label htmlFor="last-period" style={labelStyle}>{t("lastPeriodLabel")}</label>
              <input
                type="date"
                id="last-period"
                value={inputs.lastPeriod}
                onChange={(e) => setInputs({ ...inputs, lastPeriod: e.target.value })}
                style={inputStyle}
                required
              />
            </div>

            <div style={formGroupStyle}>
              <label htmlFor="cycle-length" style={labelStyle}>{t("cycleLengthLabel")}</label>
              <select
                id="cycle-length"
                value={inputs.cycleLength}
                onChange={(e) => setInputs({ ...inputs, cycleLength: parseInt(e.target.value) })}
                style={inputStyle}
                required
              >
                {Array.from({ length: 19 }, (_, i) => i + 22).map(days => (
                  <option key={days} value={days}>{days} {t("days")}</option>
                ))}
              </select>
            </div>

            <button
              type="submit"
              style={calculateButtonStyle}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 8px 20px rgba(190, 18, 60, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              {t("calculateButton")}
            </button>
          </form>

          {results && (
            <div style={resultsStyle}>
              <h3 style={resultsH3Style}>{t("resultsTitle")}</h3>

              <div style={cycleInfoStyle}>
                <div style={cycleInfoItemStyle}>
                  <span style={cycleInfoLabelStyle}>{t("cycleLengthDisplay")}</span>
                  <span style={cycleInfoValueStyle}>{results.cycleLength} {t("days")}</span>
                </div>
                <div style={cycleInfoItemStyle}>
                  <span style={cycleInfoLabelStyle}>{t("lastPeriodDisplay")}</span>
                  <span style={cycleInfoValueStyle}>{formatDate(results.lastPeriodDate)}</span>
                </div>
                <div style={{ ...cycleInfoItemStyle, borderBottom: 'none' }}>
                  <span style={cycleInfoLabelStyle}>{t("nextPeriodDisplay")}</span>
                  <span style={cycleInfoValueStyle}>{formatDate(results.nextPeriodDate)}</span>
                </div>
              </div>

              <div style={ovulationDayStyle}>
                <h4 style={ovulationH4Style}>{t("ovulationDayTitle")}</h4>
                <div style={ovulationDateStyle}>{formatDate(results.ovulationDate)}</div>
                <div style={ovulationNoteStyle}>{t("ovulationNote")}</div>
              </div>

              <div style={fertileWindowStyle}>
                <h4 style={fertileH4Style}>{t("fertileWindowTitle")}</h4>
                <p style={fertileDescStyle}>{t("fertileWindowDesc")}</p>
                <div style={fertileDatesStyle}>
                  {results.fertileDays.map((day, index) => (
                    <div key={index} style={fertileDateStyle}>
                      <div style={fertileDateLabelStyle}>
                        {index === 5 ? t("ovulationDay") : `${t("day")} ${index + 1}`}
                      </div>
                      <div style={fertileDateValueStyle}>{formatShortDate(day)}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div style={nextPeriodsStyle}>
                <h4 style={nextPeriodsH4Style}>{t("nextCyclesTitle")}</h4>
                <div style={periodListStyle}>
                  {results.nextCycles.map((cycle) => (
                    <div key={cycle.cycleNumber} style={periodItemStyle}>
                      <div style={periodCycleStyle}>{t("cycle")} {cycle.cycleNumber}</div>
                      <div style={periodDatesStyle}>
                        {t("period")}: {formatShortDate(cycle.periodStart)} | {' '}
                        {t("fertile")}: {formatShortDate(cycle.fertileStart)} - {formatShortDate(cycle.ovulation)} | {' '}
                        {t("ovulation")}: {formatShortDate(cycle.ovulation)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div style={legendStyle}>
                <div style={legendItemStyle}>
                  <div style={{ ...legendColorStyle, background: colors.primaryLight }}></div>
                  <span>{t("legendPeriod")}</span>
                </div>
                <div style={legendItemStyle}>
                  <div style={{ ...legendColorStyle, background: colors.fertileLight }}></div>
                  <span>{t("legendFertile")}</span>
                </div>
                <div style={legendItemStyle}>
                  <div style={{ ...legendColorStyle, background: colors.ovulationLight, border: `2px solid ${colors.ovulation}` }}></div>
                  <span>{t("legendOvulation")}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2GlassStyle}>{t("introTitle")}</h2>
        <p style={paragraphStyle}>{t("introParagraph1")}</p>
        <p style={paragraphStyle}>{t("introParagraph2")}</p>
        <p style={paragraphStyle}>{t("introParagraph3")}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2GlassStyle}>{t("howToUseTitle")}</h2>
        <ol style={listStyle}>
          <li style={listItemStyle}><strong style={strongStyle}>{t("step1Title")}</strong> {t("step1Desc")}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t("step2Title")}</strong> {t("step2Desc")}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t("step3Title")}</strong> {t("step3Desc")}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t("step4Title")}</strong> {t("step4Desc")}</li>
        </ol>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2GlassStyle}>{t("insightsTitle")}</h2>
        <p style={paragraphStyle}>{t("insightsParagraph1")}</p>
        <p style={paragraphStyle}>{t("insightsParagraph2")}</p>
        <p style={paragraphStyle}>{t("insightsParagraph3")}</p>
        <p style={paragraphStyle}>{t("insightsParagraph4")}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2GlassStyle}>{t("detailsTitle")}</h2>
        <h3 style={{ ...h2GlassStyle, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem', marginTop: '1.5rem' }}>{t("understandingOvulationTitle")}</h3>
        <p style={paragraphStyle}>{t("understandingOvulationDesc")}</p>

        <h3 style={{ ...h2GlassStyle, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem', marginTop: '1.5rem' }}>{t("fertileWindowTitle")}</h3>
        <p style={paragraphStyle}>{t("fertileWindowDesc2")}</p>

        <h3 style={{ ...h2GlassStyle, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem', marginTop: '1.5rem' }}>{t("trackingMethodsTitle")}</h3>
        <ul style={listStyle}>
          <li style={listItemStyle}><strong style={strongStyle}>{t("method1Title")}</strong> {t("method1Desc")}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t("method2Title")}</strong> {t("method2Desc")}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t("method3Title")}</strong> {t("method3Desc")}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t("method4Title")}</strong> {t("method4Desc")}</li>
        </ul>

        <h3 style={{ ...h2GlassStyle, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem', marginTop: '1.5rem' }}>{t("tipsTitle")}</h3>
        <ul style={listStyle}>
          <li style={listItemStyle}>{t("tip1")}</li>
          <li style={listItemStyle}>{t("tip2")}</li>
          <li style={listItemStyle}>{t("tip3")}</li>
          <li style={listItemStyle}>{t("tip4")}</li>
          <li style={listItemStyle}>{t("tip5")}</li>
        </ul>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2GlassStyle}>{t("faqTitle")}</h2>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t("faq1Question")}</h3>
          <p style={faqAnswerStyle}>{t("faq1Answer")}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t("faq2Question")}</h3>
          <p style={faqAnswerStyle}>{t("faq2Answer")}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t("faq3Question")}</h3>
          <p style={faqAnswerStyle}>{t("faq3Answer")}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t("faq4Question")}</h3>
          <p style={faqAnswerStyle}>{t("faq4Answer")}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t("faq5Question")}</h3>
          <p style={faqAnswerStyle}>{t("faq5Answer")}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t("faq6Question")}</h3>
          <p style={faqAnswerStyle}>{t("faq6Answer")}</p>
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2GlassStyle}>{t("referencesTitle")}</h2>
        <ul style={referenceListStyle}>
          <li style={referenceItemStyle}>
            [1] <a href="https://vidafertility.com/en/ovulation-calculator-ovulation-days-fertile-days/" target="_blank" rel="noopener noreferrer" style={referenceLinkStyle}>
              Ovulation and fertile days calculator: know your menstrual cycle - Vida Fertility
            </a>
          </li>
          <li style={referenceItemStyle}>
            [2] <a href="https://theboonivf.com/2024/12/10/how-to-effectively-use-an-ovulation-calculator-to-plan-pregnancy/" target="_blank" rel="noopener noreferrer" style={referenceLinkStyle}>
              How to Use an Ovulation Calculator for Pregnancy? - The Boon IVF
            </a>
          </li>
          <li style={referenceItemStyle}>
            [3] <a href="https://www.pinkishe.org/blog-post/the-ultimate-guide-to-ovulation-calculators-menstrual-tracking-and-safe-periods" target="_blank" rel="noopener noreferrer" style={referenceLinkStyle}>
              The Ultimate Guide to Ovulation Calculators, Menstrual Tracking - Pinkishe
            </a>
          </li>
          <li style={referenceItemStyle}>
            [4] <a href="https://www.mayoclinichealthsystem.org/hometown-health/speaking-of-health/using-ovulation-kits-to-predict-fertility" target="_blank" rel="noopener noreferrer" style={referenceLinkStyle}>
              Using ovulation tools to predict fertility - Mayo Clinic Health System
            </a>
          </li>
          <li style={referenceItemStyle}>
            [5] <a href="https://samarthaivf.com/understanding-ovulation-calculators-and-enhancing-fertility/" target="_blank" rel="noopener noreferrer" style={referenceLinkStyle}>
              Understanding Ovulation Calculators and Enhancing Fertility - Samartha IVF
            </a>
          </li>
          <li style={referenceItemStyle}>
            [6] <a href="https://womenshealth.gov/ovulation-calculator" target="_blank" rel="noopener noreferrer" style={referenceLinkStyle}>
              Ovulation calculator - Women's Health.gov
            </a>
          </li>
          <li style={referenceItemStyle}>
            [7] <a href="https://my.clevelandclinic.org/health/articles/17900-rhythm-method" target="_blank" rel="noopener noreferrer" style={referenceLinkStyle}>
              Rhythm Method: Birth Control, Calendar & Effectiveness - Cleveland Clinic
            </a>
          </li>
        </ul>
      </section>
    </div>
  );
}